import React, { useState } from 'react';
import './searchResults.css';
import img19 from './assets/19.png';
import img22 from './assets/22.png';
import img28 from './assets/28.png';
import img21 from './assets/21.jpg';

// --- Custom Components (Simplified Replacements for Material-UI) ---
// You would expand these with proper styling and features as needed.

const CustomButton = ({ children, onClick, disabled, className }) => (
  <button onClick={onClick} disabled={disabled} className={`custom-button ${className || ''}`}>
    {children}
  </button>
);

const CustomInput = ({ value, onChange, onKeyPress, placeholder, icon: IconComponent }) => (
  <div className="custom-input-wrapper">
    {IconComponent && <IconComponent className="custom-input-icon" />}
    <input
      type="text"
      value={value}
      onChange={onChange}
      onKeyPress={onKeyPress}
      placeholder={placeholder}
      className="custom-input-field"
    />
  </div>
);

const CustomCard = ({ children, onClick, className }) => (
  <div className={`custom-card ${className || ''}`} onClick={onClick}>
    {children}
  </div>
);

const CustomDialog = ({ isOpen, onClose, title, subtitle, children }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <div className="modal-header-top">
            <h2 className="modal-title">{title}</h2>
            <button onClick={onClose} className="modal-close-button">
              &times;
            </button>
          </div>
          {subtitle && <p className="modal-subtitle">{subtitle}</p>}
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};


const Spinner = () => (
  <div className="spinner-container">
    <div className="spinner"></div>
  </div>
);

// --- Icons (Basic SVG/Emoji replacements - you'd use proper icon libraries) ---
const SearchIcon = () => <span role="img" aria-label="search">🔍</span>;
const CloseIcon = () => <span role="img" aria-label="close">✖️</span>;
const StarIcon = () => <span role="img" aria-label="star">⭐</span>; // For full stars
const HalfStarIcon = () => <span role="img" aria-label="half-star">⭐½</span>; // Placeholder for half star

const furnitureOptions = [
  {id: 1, label: 'Fully Furnished'},
  {id: 2, label: 'Semi-Furnished'},
  {id: 3, label: 'Not Furnished'}
];

const parkingOptions = [
  {id: 1, label: 'Included'},
  {id: 2, label: 'Paid'},
  {id: 3, label: 'Street'},
  {id: 4, label: 'None'}
]

// --- Main SearchResults Component ---
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api/property/search-by-address';
const REVIEWS_API_URL = process.env.REACT_APP_BACKEND_URL + '/api/review/get-reviews-by-property-id';

const SearchResults = () => {
  const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/400x200?text=No+Image';
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsSummary, setReviewsSummary] = useState('');
  const [avgStreetRent, setAvgStreetRent] = useState(null);
  const [avgStreetRentLoading, setAvgStreetRentLoading] = useState(false);
  const [pros, setPros] = useState([]);
  const [cons, setCons] = useState([]);



  const rents = reviews.map(r => r.rent).filter(r => typeof r === 'number');
  const highestRent = rents.length ? Math.max(...rents) : null;
  const lowestRent = rents.length ? Math.min(...rents) : null;

  const handleCloseModal = () => {
    setSelectedProperty(null);
    setReviews([]);
    setReviewsSummary('');
    setAvgStreetRent(null);
  };

  const handleCardClick = async (property) => {
    setSelectedProperty(property);
    setReviewsLoading(true);
    setReviewsSummary('');
    setAvgStreetRent(null);
    setAvgStreetRentLoading(true);

    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${REVIEWS_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          property_id: property.id
        })
      });

      const data = await response.json();

      // Pull everything from the single response
      setReviews(data.reviews || []);
      setReviewsSummary(data.summary || '');
      setPros(data.pc?.pros || []);
      setCons(data.pc?.cons || []);
      setAvgStreetRent(data.avg_street_rent?.avg_rent || null);
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
      setReviews([]);
      setReviewsSummary('');
      setPros([]);
      setCons([]);
      setAvgStreetRent(null);
    } finally {
      setReviewsLoading(false);
      setAvgStreetRentLoading(false); // Optional: still useful if you show spinners separately
    }

  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    const token = localStorage.getItem('token');
    setLoading(true);
    setHasSearched(true);

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          address: searchQuery
        })
      });
      const data = await response.json();
      console.log(data)
      setProperties(data.properties || []);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const renderStars = (rating) => {
    if (typeof rating !== 'number' || rating === 0) return null;
    const rounded = Math.round(rating * 100) / 100;
    const fullStars = Math.floor(rounded);
    const hasHalfStar = rounded % 1 !== 0 && rounded > fullStars; // Check if there's a fractional part for a half-star

    const stars = [];
    for (let i = 0; i < fullStars; i++) {
      stars.push(<StarIcon key={`star-${i}`} />);
    }
    if (hasHalfStar) {
      stars.push(<HalfStarIcon key="half-star" />);
    }
    return (
      <div className="star-rating">
        {stars}
        <span className="rating-value">({rounded.toFixed(2)})</span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="page-container">
      <h1 className="page-title">Properties</h1>
      <p className="page-subtitle">Search for properties by address or neighborhood</p>

      {/* Search Section */}
      <div className="search-section">
        <CustomInput
          placeholder="Enter address or neighborhood (e.g., 123 Main St, Clifton)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <CustomButton
          onClick={handleSearch}
          disabled={!searchQuery.trim() || loading}
        >
          {loading ? <Spinner /> : 'Search'}
        </CustomButton>
      </div>

      {/* Results Section */}
      {hasSearched && (
        <>
          {loading ? (
            <div className="loading-state">
              <Spinner />
              <p>Searching for properties...</p>
            </div>
          ) : (
            <>
              <p className="results-summary">
                Found {properties.length} properties.
              </p>

              <div className="property-grid">
                {properties.map((p) => (
                  <CustomCard key={p.id} className="property-item" onClick={() => handleCardClick(p)}>
                    <div className="property-image-wrapper">
                      <img
                        src={
                          p.id === 19 ? img19 :
                          p.id === 29 ? img22 :
                          p.id === 28 ? img28 :
                          p.id === 21 ? img21 :
                          p.image_url || PLACEHOLDER_IMAGE
                        }
                        alt="Property"
                        className="property-main-image"
                      />
                    </div>
                    <div className="property-content">
                      <h3 className="property-address-line">
                        {p.street}
                      </h3>
                      <p className="property-details-line">
                        {p.num_bedrooms} bed • {p.num_bathrooms} bath • ${p.rent}/month
                      </p>

                      {p.avg_rating && (
                        <div className="property-list-rating">
                          <span>Rating:</span>
                          {renderStars(p.avg_rating)}
                        </div>
                      )}

                      <div className="property-features">
                        <p><strong>Type:</strong> {p.type === 1 ? 'Apartment' : p.type === 2 ? 'House' : 'Other'}</p>
                        <p><strong>Laundry:</strong> {p.laundry_type === 1 ? 'In-unit' : p.laundry_type === 2 ? 'Shared' : 'None'}</p>
                      </div>

                      <p className="property-cta">
                        Click to view details and reviews
                      </p>
                    </div>
                  </CustomCard>
                ))}
              </div>

              {hasSearched && properties.length === 0 && !loading && (
                <div className="no-results-found">
                  <h2>No properties found for "{searchQuery}"</h2>
                  <p>Try searching with a different address or neighborhood name.</p>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Property Details Modal */}
      <CustomDialog
        isOpen={!!selectedProperty}
        onClose={handleCloseModal}
        title={selectedProperty ? `${selectedProperty.street} ${selectedProperty.apt_no ? `, Apt ${selectedProperty.apt_no}` : ''}` : ''}
        subtitle={selectedProperty ? `${selectedProperty.city}, ${selectedProperty.state}` : ''}
      >
        {selectedProperty && (
          <div className="modal-details-content">
            {/* Image Section - First in Modal */}
            <div className="modal-property-image-large">
              <img
                src={
                  selectedProperty.id === 19 ? img19 :
                  selectedProperty.id === 29 ? img22 :
                  selectedProperty.id === 28 ? img28 :
                  selectedProperty.id === 21 ? img21 :
                  selectedProperty.image_url || PLACEHOLDER_IMAGE
                }
                alt="Property"
                className="modal-property-image-full"
              />
            </div>

            {/* Ratings - Immediately under image */}
            <div className="modal-ratings-summary">
                <h3 className="modal-section-title">Overall Ratings:</h3>
                <div className="modal-rating-item">
                    <span>Property Rating:</span> {renderStars(selectedProperty.avg_rating)}
                </div>
                {selectedProperty.maintenance_rating && (
                    <div className="modal-rating-item">
                        <span>Maintenance Rating:</span> {renderStars(selectedProperty.maintenance_rating)}
                    </div>
                )}
            </div>

            <div className="pros-cons-grid">
              {/* Pros */}
              <div className="pros-column">
                <h3>Pros</h3>
                <ul>
                  {pros.map((item, index) => (
                    <li key={`pro-${index}`} className="pros-item">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Cons */}
              <div className="cons-column">
                <h3>Cons</h3>
                <ul>
                  {cons.map((item, index) => (
                    <li key={`con-${index}`} className="cons-item">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>



            {/* Reviews Summary Section */}
            {reviewsSummary && (
              <div className="reviews-summary-section">
                <h3 className="modal-section-title">Reviews Summary</h3>
                <div className="summary-card">
                  <p className="summary-text">{reviewsSummary}</p>
                </div>
              </div>
            )}

            <div className="modal-info-grid">
              {/* Property Information Card Group */}
              <div className="info-card-group">
                <h3 className="modal-section-title">Property Information</h3>
                <div className="info-cards-container">
                  <div className="info-stat-card">
                    <p>Bedrooms</p>
                    <h4>{selectedProperty.num_bedrooms}</h4>
                  </div>
                  <div className="info-stat-card">
                    <p>Bathrooms</p>
                    <h4>{selectedProperty.num_bathrooms}</h4>
                  </div>
                  {selectedProperty.num_closets > 0 && (
                    <div className="info-stat-card">
                      <p>Closets</p>
                      <h4>{selectedProperty.num_closets}</h4>
                    </div>
                  )}
                  <div className="info-stat-card">
                    <p>Type</p>
                    <h4>{selectedProperty.type === 1 ? 'Apartment' : selectedProperty.type === 2 ? 'House' : 'Other'}</h4>
                  </div>
                  <div className="info-stat-card">
                    <p>Laundry</p>
                    <h4>{selectedProperty.laundry_type === 1 ? 'In-unit' : selectedProperty.laundry_type === 2 ? 'Shared' : 'None'}</h4>
                  </div>
                  {selectedProperty.furniture > 0 && (
                    <div className='info-stat-card'>
                      <p>Furniture</p>
                      <h4>{selectedProperty.furniture === 1 ? 'Fully Furnished' : selectedProperty.furniture === 2 ? 'Semi-Furnished' : selectedProperty.furniture === 3 ? 'Not Furnished' : 'Unkown'}</h4>
                    </div>
                  )}
                  <div className="info-stat-card">
                    <p>Trash</p>
                    <h4>{selectedProperty.trash === 1 ? 'Included' : selectedProperty.trash === 2 ? 'Separate' : 'Unknown'}</h4>
                  </div>
                  {selectedProperty.parking > 0 && (
                    <div className="info-stat-card">
                      <p>Parking</p>
                      <h4>{selectedProperty.parking === 1 ? 'Included' : selectedProperty.parking ===2 ? 'Paid' : selectedProperty.parking === 3 ? 'Street' : selectedProperty.parking === 4 ? 'None' : 'Unknown' }</h4>
                    </div>
                  )}
                </div>
              </div>

              {/* Rent Details Card Group */}
              <div className="info-card-group">
                <h3 className="modal-section-title">Rent & Costs</h3>
                <div className="info-cards-container">
                  <div className="info-stat-card">
                    <p>Current Rent</p>
                    <h4>${selectedProperty.rent}/month</h4>
                  </div>
                  {avgStreetRentLoading ? (
                    <div className="info-stat-card">
                      <p>Average Street Rent</p>
                      <div className="loading-small">
                        <Spinner />
                      </div>
                    </div>
                  ) : avgStreetRent ? (
                    <div className="info-stat-card">
                      <p>Average Street Rent</p>
                      <h4>${avgStreetRent}/month</h4>
                    </div>
                  ) : null}
                  {highestRent && (
                    <div className="info-stat-card">
                      <p>Highest Reported Rent</p>
                      <h4>${highestRent}/month</h4>
                    </div>
                  )}
                  {lowestRent && (
                    <div className="info-stat-card">
                      <p>Lowest Reported Rent</p>
                      <h4>${lowestRent}/month</h4>
                    </div>
                  )}
                  {selectedProperty.zillow_price > 0 && (
                    <div className="info-stat-card">
                      <p>Zillow Estimate</p>
                      <h4>${selectedProperty.zillow_price}</h4>
                    </div>
                  )}
                </div>
                <br></br>
                <h3 className="modal-section-title">Additional Fees</h3>
                  <div className="info-cards-container">
                    {(
                      <div className="info-stat-card">
                        <p>Sign on Fees</p>
                        <h4>${selectedProperty.sign_on_fees === null ? 0 : selectedProperty.sign_on_fees}</h4>
                      </div>
                    )}
                    {selectedProperty.parking_fees > 0 && (
                      <div className="info-stat-card">
                        <p>Parking Fees</p>
                        <h4>${selectedProperty.parking_fees === null ? 0 : selectedProperty.parking_fees}</h4>
                      </div>
                    )}
                </div>
              </div>

              {/* Bills Card Group */}
              {(selectedProperty.energy_bill > 0 || selectedProperty.water_bill > 0 || selectedProperty.unified_bill > 0) && (
                <div className="info-card-group">
                  <h3 className="modal-section-title">Bills (Monthly Average)</h3>
                  <div className="info-cards-container">
                    {selectedProperty.energy_bill > 0 && (
                      <div className="info-stat-card">
                        <p>Energy</p>
                        <h4>${selectedProperty.energy_bill}/month</h4>
                      </div>
                    )}
                    {selectedProperty.water_bill > 0 && (
                      <div className="info-stat-card">
                        <p>Water</p>
                        <h4>${selectedProperty.water_bill}/month</h4>
                      </div>
                    )}
                    {selectedProperty.unified_bill > 0 && (
                      <div className="info-stat-card">
                        <p>Unified</p>
                        <h4>${selectedProperty.unified_bill}/month</h4>
                      </div>
                    )}
                  </div>
                  <br></br>
                </div>
              )}
            </div> {/* End modal-info-grid */}

            <hr className="modal-divider" />

            {/* Reviews Section */}
            <div className="reviews-section">
              <h2 className="modal-section-title">Reviews</h2>
              {reviewsLoading ? (
                <div className="loading-reviews">
                  <Spinner />
                  <p>Loading reviews...</p>
                </div>
              ) : reviews.length === 0 ? (
                <p className="no-reviews">No reviews available for this property.</p>
              ) : (
                <div className="reviews-list">
                  {reviews.map((review) => (
                    <div key={review.id} className="review-card">
                      <div className="review-header">
                        <span className="review-rating-display">Property Rating: {renderStars(review.stars)}</span>
                        {review.maintenance_rating && (
                          <span className="review-maintenance-rating-display">Maintenance: {renderStars(review.maintenance_rating)}</span>
                        )}
                        <span className="review-date">{formatDate(review.created_at)}</span>
                      </div>
                      <p className="review-text-content">
                        {review.review_text}
                      </p>
                      <div className="review-basic-details">
                        <span>{review.bedrooms} bed • {review.bathrooms} bath • ${review.rent}/month</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </CustomDialog>
    </div>
  );
};

export default SearchResults;